/**
 * PalletPhotoDialog — upload + browse photos attached to a single
 * pallet movement row. Mirrors the dispatch PhotoCaptureDialog UX
 * (file/camera input + base64 preview) so the floor learning curve is
 * zero. Most-common use-case is loss/damage evidence, so the category
 * defaults to "damage" when the row's kind is `loss` or `damage`.
 */
import React, { useEffect, useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import { Camera, Trash, ImageSquare, FilePdf, PaperPlaneTilt } from '@phosphor-icons/react';
import {
  addPalletMovementPhoto, listPalletMovementPhotos, deletePalletMovementPhoto,
  palletClaimPdfUrl, sendPalletClaimEmail,
} from '../../lib/api';
import { fmtDateTime } from './utils';

const CATEGORIES = ['damage', 'loss', 'condition', 'delivery', 'other'];

export default function PalletPhotoDialog({ open, setOpen, movement, isManager, onRefresh }) {
  // Default category infers from the movement kind so a damage row opens
  // pre-tagged for damage — saves one click on every claim.
  const initialCategory =
    movement?.kind === 'loss' ? 'loss' :
    movement?.kind === 'damage' ? 'damage' : 'condition';

  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState(initialCategory);
  const [caption, setCaption] = useState('');
  const [base64, setBase64] = useState('');
  const [busy, setBusy] = useState(false);

  // Send-claim sub-form state. Local to the dialog — collapsed by default
  // so the dialog stays compact for the common (just-attach-a-photo) flow.
  const [sendOpen, setSendOpen] = useState(false);
  const [sendTo, setSendTo] = useState('');
  const [sendNote, setSendNote] = useState('');
  const [sending, setSending] = useState(false);
  const [lastSent, setLastSent] = useState(null);

  const refresh = async () => {
    if (!movement) return;
    setLoading(true);
    try {
      const r = await listPalletMovementPhotos(movement.id);
      setPhotos(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load photos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !movement) return;
    setCategory(initialCategory);
    setCaption('');
    setBase64('');
    setSendOpen(false);
    setSendTo('');
    setSendNote('');
    setLastSent(movement.last_claim_sent_at ? {
      to: movement.last_claim_sent_to,
      at: movement.last_claim_sent_at,
      status: movement.last_claim_sent_status,
    } : null);
    refresh();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [open, movement?.id]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error('Max 5 MB');
      return;
    }
    const r = new FileReader();
    r.onload = () => setBase64(String(r.result || ''));
    r.readAsDataURL(f);
  };

  const upload = async () => {
    if (!base64) return toast.error('Select a photo first');
    setBusy(true);
    try {
      await addPalletMovementPhoto(movement.id, {
        base64, category, caption: caption || null,
      });
      toast.success('Photo attached');
      setBase64('');
      setCaption('');
      await refresh();
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await deletePalletMovementPhoto(movement.id, photoId);
      toast.success('Photo deleted');
      await refresh();
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const sendClaim = async () => {
    if (!photos.length) return toast.error('Attach at least one photo first');
    setSending(true);
    try {
      const payload = {};
      if (sendTo.trim()) payload.to = sendTo.trim();
      if (sendNote.trim()) payload.note = sendNote.trim();
      const r = await sendPalletClaimEmail(movement.id, payload);
      const d = r.data;
      const where = d.to;
      const status = d.mocked ? 'mocked (RESEND_API_KEY not set)' : (d.status || 'sent');
      toast.success(`Claim emailed to ${where} · ${status}`);
      setLastSent({ to: where, at: new Date().toISOString(), status: d.status });
      setSendOpen(false);
      setSendTo('');
      setSendNote('');
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pallet-photo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera size={18} /> Photos · {movement?.type_code} ·{' '}
            <span className="text-xs uppercase tracking-wider px-1.5 py-0.5 bg-slate-200">
              {movement?.kind}
            </span>
          </DialogTitle>
          <DialogDescription>
            Attach loss/damage evidence so claims with the pallet pooler (CHEP/Loscam) carry visual proof.
          </DialogDescription>
        </DialogHeader>

        {/* Claim PDF — only shown for loss/damage rows. Opens in a new
            tab so the operator can review before emailing to the pooler.
            Disabled until at least one photo is attached so we never
            email a packet with no evidence. */}
        {(movement?.kind === 'loss' || movement?.kind === 'damage') && (
          <div className="bg-amber-50 border border-amber-200 p-2 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-xs text-amber-900">
                <strong>Claim packet</strong> · bundles this movement + photos into a single PDF for CHEP/Loscam claims.
              </div>
              <div className="flex items-center gap-2">
                {photos.length > 0 ? (
                  <a href={palletClaimPdfUrl(movement.id)}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-amber-600 text-white hover:bg-amber-700"
                    data-testid="pallet-claim-pdf-btn">
                    <FilePdf size={14} weight="bold" /> Generate claim PDF
                  </a>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-slate-200 text-slate-500 cursor-not-allowed"
                    title="Attach at least one photo first">
                    <FilePdf size={14} /> Generate claim PDF
                  </span>
                )}
                {photos.length > 0 ? (
                  <button onClick={() => setSendOpen((v) => !v)}
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-emerald-600 text-white hover:bg-emerald-700"
                    data-testid="pallet-claim-send-toggle">
                    <PaperPlaneTilt size={14} weight="bold" /> Send claim by email
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-slate-200 text-slate-500 cursor-not-allowed"
                    title="Attach at least one photo first">
                    <PaperPlaneTilt size={14} /> Send claim by email
                  </span>
                )}
              </div>
            </div>

            {lastSent && (
              <div className="text-[11px] text-amber-900 bg-amber-100/60 border border-amber-200 px-2 py-1"
                data-testid="pallet-claim-last-sent">
                Last sent to <strong>{lastSent.to}</strong> · {fmtDateTime(lastSent.at)}
                {lastSent.status && ` · ${lastSent.status}`}
              </div>
            )}

            {sendOpen && photos.length > 0 && (
              <div className="bg-white border border-amber-200 p-2 space-y-2" data-testid="pallet-claim-send-form">
                <div>
                  <Label className="text-[11px]">To (override default pooler / merchant email)</Label>
                  <Input type="email" value={sendTo} onChange={(e) => setSendTo(e.target.value)}
                    placeholder="claims@chep.com.au"
                    data-testid="pallet-claim-send-to" />
                </div>
                <div>
                  <Label className="text-[11px]">Note (optional, included in email body)</Label>
                  <Input value={sendNote} onChange={(e) => setSendNote(e.target.value)}
                    placeholder="Damage caused during inbound on Bay 7…"
                    data-testid="pallet-claim-send-note" />
                </div>
                <Button onClick={sendClaim} disabled={sending}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="pallet-claim-send-submit">
                  {sending ? 'Sending…' : 'Send claim email'}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Existing photos grid */}
        <section data-testid="pallet-photo-gallery">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-xs uppercase tracking-wider font-bold text-muted-foreground">
              {loading ? 'Loading…' : `Attached photos · ${photos.length}`}
            </h3>
          </div>
          {!loading && photos.length === 0 && (
            <div className="text-sm text-muted-foreground p-4 border border-dashed border-border bg-muted/30 flex items-center gap-2">
              <ImageSquare size={16} /> No photos yet — upload below.
            </div>
          )}
          {photos.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {photos.map((p) => (
                <div key={p.photo_id} className="border border-border bg-card overflow-hidden flex flex-col"
                  data-testid={`pallet-photo-${p.photo_id}`}>
                  <img src={p.base64} alt={p.caption || p.category} className="w-full h-32 object-cover" />
                  <div className="p-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-200">{p.category}</span>
                      {isManager && (
                        <button onClick={() => remove(p.photo_id)}
                          className="text-rose-700 hover:text-rose-900"
                          data-testid={`pallet-photo-delete-${p.photo_id}`}>
                          <Trash size={12} />
                        </button>
                      )}
                    </div>
                    {p.caption && <div className="mt-1 text-[11px]">{p.caption}</div>}
                    <div className="text-[10px] text-muted-foreground mt-0.5">{fmtDateTime(p.uploaded_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Uploader */}
        <section className="border-t border-border pt-3 space-y-3" data-testid="pallet-photo-uploader">
          <h3 className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Add photo</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}
                className="w-full border border-border bg-background h-9 px-2 text-sm"
                data-testid="pallet-photo-category">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label>Photo (≤ 5 MB)</Label>
              <input type="file" accept="image/*" capture="environment" onChange={onFile}
                className="text-xs h-9" data-testid="pallet-photo-file-input" />
            </div>
          </div>
          <div>
            <Label>Caption</Label>
            <Input value={caption} onChange={(e) => setCaption(e.target.value)}
              placeholder="e.g. Cracked stringer · pallet 7 of 12"
              data-testid="pallet-photo-caption" />
          </div>
          {base64 && <img src={base64} alt="preview" className="max-h-48 border border-border" />}
          <Button onClick={upload} disabled={busy || !base64} className="w-full"
            data-testid="pallet-photo-upload-btn">
            {busy ? 'Uploading…' : 'Attach photo'}
          </Button>
        </section>
      </DialogContent>
    </Dialog>
  );
}
